import React from "react";
import { IconButton, Modal as MuiModal, ModalProps, Fade, Backdrop } from "@mui/material";
import Close from "@mui/icons-material/Close";
import styled, { FlattenSimpleInterpolation } from "styled-components";

export type CustomModalProps = ModalProps & {
  /** Replaces the default "X" close button */
  CustomCloseButton?: React.ReactNode;
  /** Called when clicking the close button, backdrop, or on "Esc" keydown */
  handleClose?: () => void;
  fadeProps?: Partial<React.ComponentProps<typeof Fade>>;
  backdropProps?: Partial<React.ComponentProps<typeof Backdrop>>;
  customCss?: string | FlattenSimpleInterpolation;
};

export const Modal = ({
  children,
  open,
  CustomCloseButton,
  handleClose,
  onClick,
  customCss,
  fadeProps = {},
  backdropProps = {},
  ...modalProps
}: CustomModalProps): React.JSX.Element => {
  return (
    <StyledModal
      {...{
        open,
        closeAfterTransition: true,
        BackdropComponent: modalProps.hideBackdrop ? () => <></> : Backdrop,
        BackdropProps: { timeout: 200, ...fadeProps, ...backdropProps },
        $customCss: customCss,
      }}
      {...modalProps}
      onClose={(event: Event) => {
        // Need to catch clicks on the backdrop so they don't go through and
        // click the main screen again.
        event.stopPropagation();
        if (handleClose) {
          handleClose();
        }
      }}
    >
      <Fade in={open} {...fadeProps}>
        {
          // Fade children needs to be wrapped in a DOM element (e.g. div) to
          // forward the ref https://mui.com/guides/migration-v4/#cannot-read-property-scrolltop-of-null
        }
        <div className="container">
          {children}
          {CustomCloseButton || (
            <IconButton
              className="btnClose"
              onClick={(e: any) => {
                e.stopPropagation();
                if (handleClose) {
                  handleClose();
                }
              }}
              size="large"
            >
              <Close />
            </IconButton>
          )}
        </div>
      </Fade>
    </StyledModal>
  );
};

const StyledModal = styled(MuiModal)<{ $customCss?: string | FlattenSimpleInterpolation }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background: black;
  width: 100%;
  height: 100%;
  /* padding: 56px 64px; */
  position: relative;

  .container {
    width: 100%;
    height: 100%;
    z-index: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .btnClose {
    position: absolute;
    top: 24px;
    right: 24px;
    color: white;
    .MuiSvgIcon-root {
      transform: scale(2.5);
    }
  }
  ${({ $customCss }) => $customCss || ""}
`;
